import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Roles() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", label: "", permissions: [] });

  const { data: roles } = useQuery({ queryKey: ["roles"], queryFn: async () => (await api.get("/roles")).data });
  const { data: allPerms } = useQuery({ queryKey: ["permissions"], queryFn: async () => (await api.get("/permissions")).data });

  useEffect(() => {
    if (open) setForm(editing ? { name: editing.name, label: editing.label, permissions: editing.permissions } : { name: "", label: "", permissions: [] });
  }, [open, editing]);

  const togglePerm = (p) =>
    setForm((f) => ({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p] }));

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/roles/${editing.name}`, form);
        toast.success("Role updated");
      } else {
        await api.post("/roles", form);
        toast.success("Role created");
      }
      setOpen(false);
      setEditing(null);
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (name) => {
    if (!window.confirm(`Delete role "${name}"?`)) return;
    try {
      await api.delete(`/roles/${name}`);
      toast.success("Role deleted");
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="roles-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter text-slate-900">Roles & Permissions</h1>
          <p className="mt-1 text-sm text-slate-500">Create custom roles and control access to each capability</p>
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="new-role-button" className="bg-[#EA580C] font-semibold text-white hover:bg-[#C2410C]">
          <Plus size={16} className="mr-1" /> New Role
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="roles-grid">
        {(roles || []).map((r) => (
          <div key={r.name} className="rounded-md border border-slate-200 bg-white p-5 transition-shadow hover:shadow-sm" data-testid={`role-card-${r.name}`}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-heading text-base font-bold tracking-tight text-slate-900">{r.label}</h3>
                <span className="text-xs text-slate-400">{r.name}{r.is_system ? " · system" : ""}</span>
              </div>
              <div className="flex gap-1">
                {r.name !== "admin" && (
                  <button onClick={() => { setEditing(r); setOpen(true); }} data-testid={`role-edit-${r.name}`} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                    <PencilSimple size={16} />
                  </button>
                )}
                {!r.is_system && (
                  <button onClick={() => remove(r.name)} data-testid={`role-delete-${r.name}`} className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600">
                    <Trash size={16} />
                  </button>
                )}
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {r.permissions.includes("*") ? (
                <span className="rounded-full bg-[#FFF7ED] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#C2410C]">Full access</span>
              ) : (
                r.permissions.map((p) => (
                  <span key={p} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">{p}</span>
                ))
              )}
              {r.permissions.length === 0 && <span className="text-xs text-slate-400">No permissions</span>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="role-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold tracking-tight">{editing ? `Edit: ${editing.label}` : "New Role"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!editing && (
              <div className="space-y-1.5">
                <Label>Role key</Label>
                <Input data-testid="role-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="production_manager" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Display name</Label>
              <Input data-testid="role-label-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Production Manager" />
            </div>
            <div className="space-y-2">
              <Label>Permissions</Label>
              <div className="grid grid-cols-1 gap-2 rounded-md border border-slate-200 p-3">
                {(allPerms || []).map((p) => (
                  <label key={p} className="flex items-center gap-2.5 text-sm text-slate-700" data-testid={`role-perm-${p.replace(/\./g, "-")}`}>
                    <Checkbox checked={form.permissions.includes(p)} onCheckedChange={() => togglePerm(p)} />
                    {p}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} data-testid="role-save-button" className="bg-[#EA580C] font-semibold text-white hover:bg-[#C2410C]">Save role</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
